import { useCallback, useEffect, useMemo, useState } from "react";
import { apiDeleteRequest, apiGetRequest, apiPostRequest, apiRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import { useMockSavedAnnouncements, useMockSavedAnswers } from "./useGrowCommunity";
import { mockAnswer } from "../mockData";
import { mockAnnouncement } from "../announcementMock";

// The two AI tools of "Grow your community" (COMY1-373): reply to a question
// and write a community message, plus their saved lists. Backed by
// Admin_system /api/community/ai/*, which takes the community from the
// session. In the dev-only `?preview=1` mode (no backend) they keep using the
// mocks, so the page can still be reviewed without a server.
//
// Both hooks return the same shape in either mode:
//   items, status ("loading" | "ready" | "error"), reload,
//   ask(request) → the saved item, remove(id), and for answers rename(id, name),
//   toggleStep(id, index, done) and refresh(id, uiLang) (ask again, same message).

export const isGrowPreview = () => new URLSearchParams(window.location.search).get("preview") === "1";

/** A translation key for what went wrong, from the API's error `code`. */
export const aiErrorKey = (error) => {
  const code = error?.response?.data?.code;
  switch (code) {
    case "AI_DAILY_LIMIT":
      return "ai.errors.daily_limit";
    case "AI_IMAGE_TOO_LARGE":
      return "ai.errors.image_too_large";
    case "AI_IMAGE_TYPE":
      return "ai.errors.image_type";
    case "AI_TEXT_TOO_LONG":
      return "ai.errors.text_too_long";
    default:
      return "ai.errors.unavailable";
  }
};

/** Fetches a saved list once, and keeps it in sync with what this page changes. */
const useApiList = (endpoint, key, enabled) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(enabled ? "loading" : "ready");

  const reload = useCallback(async () => {
    if (!enabled) return;
    setStatus("loading");
    try {
      const response = await apiGetRequest(endpoint);
      setItems(response.data?.[key] ?? []);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [enabled, endpoint, key]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { items, setItems, status, reload };
};

const useApiAnswers = (enabled) => {
  const { items, setItems, status, reload } = useApiList(endpoints.communityAiAnswers, "answers", enabled);

  const ask = useCallback(
    async ({ text, imageFile, askerName, source, uiLang }) => {
      // A screenshot goes as multipart; text as plain JSON.
      let response;
      if (imageFile) {
        const form = new FormData();
        form.append("image", imageFile);
        form.append("askerName", askerName || "");
        form.append("source", source);
        form.append("uiLang", uiLang);
        response = await apiPostRequest(endpoints.communityAiReply, form, { "Content-Type": "multipart/form-data" });
      } else {
        response = await apiPostRequest(endpoints.communityAiReply, { text, askerName, source, uiLang });
      }
      const { answer } = response.data;
      setItems((list) => [answer, ...list]);
      return answer;
    },
    [setItems],
  );

  const rename = useCallback(
    async (id, askerName) => {
      const response = await apiRequest("PATCH", `${endpoints.communityAiAnswers}/${id}`, { data: { askerName } });
      const { answer } = response.data;
      setItems((list) => list.map((item) => (item.id === id ? answer : item)));
    },
    [setItems],
  );

  const replace = useCallback(
    (answer) => setItems((list) => list.map((item) => (item.id === answer.id ? answer : item))),
    [setItems],
  );

  const toggleStep = useCallback(
    async (id, index, done) => {
      const response = await apiRequest("PATCH", `${endpoints.communityAiAnswers}/${id}`, { data: { step: { index, done } } });
      replace(response.data.answer);
    },
    [replace],
  );

  const refresh = useCallback(
    async (id, uiLang) => {
      const response = await apiPostRequest(`${endpoints.communityAiAnswers}/${id}/refresh`, { uiLang });
      replace(response.data.answer);
      return response.data.answer;
    },
    [replace],
  );

  const remove = useCallback(
    async (id) => {
      await apiDeleteRequest(`${endpoints.communityAiAnswers}/${id}`);
      setItems((list) => list.filter((item) => item.id !== id));
    },
    [setItems],
  );

  return { answers: items, status, reload, ask, rename, toggleStep, refresh, remove };
};

const useApiAnnouncements = (enabled) => {
  const { items, setItems, status, reload } = useApiList(endpoints.communityAiAnnouncements, "announcements", enabled);

  const ask = useCallback(
    async ({ brief, includeJoinLink, uiLang }) => {
      const response = await apiPostRequest(endpoints.communityAiAnnouncement, { brief, includeJoinLink, uiLang });
      const { announcement } = response.data;
      setItems((list) => [announcement, ...list]);
      return announcement;
    },
    [setItems],
  );

  const remove = useCallback(
    async (id) => {
      await apiDeleteRequest(`${endpoints.communityAiAnnouncements}/${id}`);
      setItems((list) => list.filter((item) => item.id !== id));
    },
    [setItems],
  );

  return { announcements: items, status, reload, ask, remove };
};

/** Saved answers from the "reply to a question" tool. */
export const useSavedAnswers = (lang) => {
  const preview = useMemo(isGrowPreview, []);
  const mock = useMockSavedAnswers(lang);
  const api = useApiAnswers(!preview);

  const mockApi = useMemo(
    () => ({
      answers: mock.answers,
      status: "ready",
      reload: () => {},
      ask: async ({ text, imageFile, askerName, source, uiLang }) => {
        const answer = await mockAnswer({ text, hasImage: !!imageFile, uiLang, askerName, source });
        mock.addAnswer(answer);
        return answer;
      },
      rename: async (id, askerName) => {
        const current = mock.answers.find((answer) => answer.id === id);
        mock.updateAnswer(id, { asker: { ...current?.asker, name: askerName, detected: false } });
      },
      toggleStep: async (id, index, done) => {
        const current = mock.answers.find((answer) => answer.id === id);
        mock.updateAnswer(id, { steps: current.steps.map((step, i) => (i === index ? { ...step, done } : step)) });
      },
      refresh: async (id, uiLang) => {
        const current = mock.answers.find((answer) => answer.id === id);
        const fresh = await mockAnswer({ text: current.input.text, uiLang, askerName: current.asker?.name, source: current.asker?.source });
        const { status, verdict, steps, replies, replyLang } = fresh;
        mock.updateAnswer(id, { status, verdict, steps, replies, replyLang });
      },
      remove: async (id) => mock.removeAnswer(id),
    }),
    [mock],
  );

  return preview ? mockApi : api;
};

/** Saved messages from the "community message" tool. */
export const useSavedAnnouncements = (lang) => {
  const preview = useMemo(isGrowPreview, []);
  const mock = useMockSavedAnnouncements(lang);
  const api = useApiAnnouncements(!preview);

  const mockApi = useMemo(
    () => ({
      announcements: mock.announcements,
      status: "ready",
      reload: () => {},
      ask: async (request) => {
        const announcement = await mockAnnouncement(request);
        mock.addAnnouncement(announcement);
        return announcement;
      },
      remove: async (id) => mock.removeAnnouncement(id),
    }),
    [mock],
  );

  return preview ? mockApi : api;
};
